import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Textarea } from '@/components/ui/textarea'
import RaidPlan, { RaidPlanTimeline } from 'app/frontend/types/custom/RaidPlan'
import {
  importFromEventActivityNote,
  ImportFromEventActivityNoteErrorCode,
} from './importFromEventActivityNote'
import {
  hasCustomRaidPlanContent,
  mergeEncounterAssignments,
  mergeRaidPlanTimeline,
  type RaidPlanTimelineMergeMode,
} from './raidPlanTransfer'

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  raidPlan: RaidPlan
  encounterWclId?: number
  timeline: RaidPlanTimeline
  rosterMemberships: wowaudit.TeamMembership[]
  onImported: (timeline: RaidPlanTimeline) => void
}

const ImportFromNoteDialog = ({
  open,
  onOpenChange,
  raidPlan,
  encounterWclId,
  timeline,
  rosterMemberships,
  onImported,
}: Props) => {
  const { t } = useTranslation()
  const [noteInput, setNoteInput] = useState('')
  const [errorCode, setErrorCode] =
    useState<ImportFromEventActivityNoteErrorCode | null>(null)

  const errorTranslationKey = useMemo(() => {
    if (!errorCode) return null
    return `dashboard.team.plans.onboarding.note-import.errors.${errorCode}`
  }, [errorCode])

  const handleImport = (mode: RaidPlanTimelineMergeMode) => {
    const result = importFromEventActivityNote({
      note: noteInput,
      raidPlan,
      encounterWclId,
      timeline,
      rosterMemberships,
    })
    if ('errorCode' in result) {
      setErrorCode(result.errorCode)
      return
    }
    const merged = mergeRaidPlanTimeline(timeline, result.timeline, mode)
    onImported(
      mode === 'cooldowns'
        ? merged
        : {
            ...merged,
            encounter_assignments: mergeEncounterAssignments(
              timeline.encounter_assignments,
              result.timeline.encounter_assignments,
            ),
          },
    )
    setErrorCode(null)
    setNoteInput('')
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-3xl'>
        <DialogHeader>
          <DialogTitle>
            {t('dashboard.team.plans.onboarding.note-import.title')}
          </DialogTitle>
        </DialogHeader>
        <div className='space-y-3'>
          <p>{t('dashboard.team.plans.onboarding.note-import.description')}</p>
          {hasCustomRaidPlanContent(timeline) ? (
            <p>
              {t('dashboard.team.plans.onboarding.note-import.replace-warning')}
            </p>
          ) : null}
          <Textarea
            value={noteInput}
            onChange={(event) => {
              setNoteInput(event.target.value)
              if (errorCode) setErrorCode(null)
            }}
            rows={5}
            placeholder={t(
              'dashboard.team.plans.onboarding.note-import.placeholder',
            )}
          />
          {errorTranslationKey ? (
            <p className='text-sm text-destructive-foreground'>
              {t(errorTranslationKey)}
            </p>
          ) : null}
          <div className='flex justify-end gap-2 flex-wrap'>
            <Button
              type='button'
              variant='outline'
              onClick={() => onOpenChange(false)}
            >
              {t('shared.cancel')}
            </Button>
            <Button
              type='button'
              variant='outline'
              onClick={() => handleImport('assignments')}
            >
              {t(
                'dashboard.team.plans.onboarding.note-import.submit-assignments',
              )}
            </Button>
            <Button
              type='button'
              variant='outline'
              onClick={() => handleImport('cooldowns')}
            >
              {t(
                'dashboard.team.plans.onboarding.note-import.submit-cooldowns',
              )}
            </Button>
            <Button type='button' onClick={() => handleImport('all')}>
              {t('dashboard.team.plans.onboarding.note-import.submit-all')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default ImportFromNoteDialog
